import React from "react";
import Helmet from "react-helmet";
import { Link } from "@reach/router";

import Navigation from "../../components/WaveNavigation";

import "./faq.scss";
import freightboatimage from "../../assets/img/freightservicemap.webp";

const slug = (s) =>
  s
    .toLowerCase()
    .replace(/[^\w]+/g, "-")
    .replace(/^-|-$/g, "");

const Faq = ({ q, children }) => (
  <details className="faq-item" id={slug(q)}>
    <summary>
      <span className="faq-q">{q}</span>
      <i className="far fa-chevron-down faq-chevron" />
    </summary>
    <div className="faq-a">{children}</div>
  </details>
);

const Section = ({ title, icon, children }) => (
  <section className="faq-section">
    <h2 className="faq-section-title">
      <span className="faq-section-icon" aria-hidden="true">
        <i className={`far fa-${icon}`} />
      </span>
      {title}
    </h2>
    {children}
  </section>
);

// NOTE: class component (not hooks). react-static 6 has a nested copy of
// react-dom in its own node_modules; hooks called during SSR hit React
// error #307 ("more than one copy of React"). Class lifecycle methods SSR
// cleanly with both copies.
class FaqPage extends React.Component {
  componentDidMount() {
    this.openFromHash();
    window.addEventListener("hashchange", this.openFromHash);
  }

  componentWillUnmount() {
    window.removeEventListener("hashchange", this.openFromHash);
  }

  // Deep-link: if the URL has a #hash matching an FAQ id, open + scroll to it.
  openFromHash = () => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el && el.tagName === "DETAILS") {
      el.open = true;
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
        50,
      );
    }
  };

  render() {
    return (
      <div>
        <Helmet
          title="Fire Island Ferries | FAQ"
          meta={[
            {
              name: "description",
              content: "Fire Island Ferries Frequently Asked Questions",
            },
            {
              name: "keywords",
              content:
                "Fire Island, New York, Long Island, Ferries, Water Taxi",
            },
          ]}
        />
        <Navigation />
        <div className="container page">
          <div className="heading">
            <h2>FAQ</h2>
            <h1>Frequently Asked Questions</h1>
          </div>
          <p className="faq-intro">
            Welcome aboard. Below are the questions we get most often — click
            any to expand. Don't see yours? Call us at{" "}
            <i className="far fa-phone" /> 631.665.3600 or email{" "}
            <i className="far fa-envelope" /> info@fireislandferries.com.
          </p>

          <Section title="Getting There" icon="compass">
            <Faq q="Where do I catch the ferry?">
              <p>
                Fire Island Ferries depart from Bay Shore, NY — about an hour
                from NYC by LIRR. For April through October, departures are
                split between our Bay Shore terminals:
              </p>
              <ul>
                <li>
                  <strong>West Terminal</strong> (104 Maple Ave): Seaview and
                  Ocean Bay Park
                </li>
                <li>
                  <strong>Saltaire Terminal</strong> (123 Maple Ave): Boats for
                  Kismet and Saltaire
                </li>
                <li>
                  <strong>Main Terminal</strong> (99 Maple Ave): Ocean Beach,
                  Fair Harbor, Dunewood, and Atlantique
                </li>
                <li>
                  <strong>Bay Shore Marina</strong> (S Clinton Ave): Specific
                  Atlantique runs during the peak Summer Schedule
                </li>
              </ul>
              <p>
                Always check your specific trip details and changeover dates
                before you head to the dock.{" "}
                <Link to="/schedules">See schedules</Link>.
              </p>
            </Faq>

            <Faq q="How do I get from the LIRR to the ferry?">
              <p>
                David Bros. taxis meet every arriving train at Bay Shore
                station. If you'd rather walk, it's about a mile south — Fourth
                Ave turns into Maple Ave. Uber and Lyft also run reliably to
                both terminals.
              </p>
            </Faq>

            <Faq q="Where do I park?">
              <p>
                On-site lots offer daily, overnight, and long-term parking when
                capacity allows.
              </p>
              <ul>
                <li>
                  Heading to Atlantique? Use the Bay Shore Marina dock lot —
                  typically more space and lower rates.
                </li>
                <li>
                  Bay Shore Marina (a few blocks away) is a solid overflow
                  option.
                </li>
                <li>
                  Summer weekends and holidays fill fast. Build in extra time.
                </li>
              </ul>
              <p>
                For current rates and capacity, call{" "}
                <i className="far fa-phone" /> 631.665.3600.{" "}
                <Link to="/info/parking">Full parking guide →</Link>
              </p>
            </Faq>

            <Faq q="How long is the boat ride?">
              <p>About 30 minutes one-way to most communities.</p>
            </Faq>
          </Section>

          <Section title="Tickets &amp; Pricing" icon="ticket-alt">
            <Faq q="Do I need a reservation?">
              <p>
                No, Fire Island Ferries tickets do not require advance booking
                or reservations.
              </p>
            </Faq>

            <Faq q="Where do I buy tickets?">
              <p>Three ways:</p>
              <ul>
                <li>
                  <strong>mTickets or Uber app</strong> (iPhone + Android) —
                  easiest, accepts credit card
                </li>
                <li>
                  <strong>Ticket window at the terminal</strong> — cash, credit,
                  or Apple Pay
                </li>
              </ul>
            </Faq>

            <Faq q="How do mobile tickets work?">
              <p>
                Download the mTickets app, buy your ticket in-app, and activate
                it <strong>only when you're at the dock about to board</strong>.
                Activated tickets expire on a timer — don't activate at home, on
                the train, or in the parking lot, or you'll have to buy another.
              </p>
              {/* TODO: replace with link to walkthrough video once produced */}
              {/* <p>
                <em>How-to video coming soon.</em>
              </p> */}
            </Faq>

            <Faq q="Can I use a round-trip ticket on a different day?">
              <p>
                Yes. Round-trip tickets are valid any day, from any community,
                however if you purchased on the mTickets app, the ticket is only
                valid for 180 days from purchase.
              </p>
            </Faq>

            <Faq q="Is there a senior discount?">
              <p>
                Yes — present a valid Suffolk County Senior Citizen ID at the
                ticket window for a discounted one-way fare.
              </p>
            </Faq>
          </Section>

          <Section title="On the Boat — What to Bring" icon="ship">
            <Faq q="Are there bathrooms on the boat?">
              <p>
                No bathrooms on the ferry. Restrooms are available at the Bay
                Shore terminal — go before you board.
              </p>
            </Faq>

            <Faq q="Are dogs allowed?">
              <p>Yes — leashed dogs are welcome and ride at the child rate.</p>
              <p>A few things to know before you bring your pup:</p>
              <ul>
                <li>
                  The Fire Island National Seashore restricts pets on ocean
                  beaches at Lighthouse, Sailors Haven, Watch Hill, Talisman,
                  and the Wilderness from March 15 through Labor Day.
                </li>
                <li>
                  Some communities have additional beach restrictions for dogs.
                </li>
                <li>
                  Call <i className="far fa-phone" /> 631.665.3600 if you want
                  to confirm the rules for a specific community.
                </li>
              </ul>
            </Faq>

            <Faq q="Can I bring a cooler, wagon, or stroller?">
              <p>Yes — and here's the breakdown:</p>
              <ul>
                <li>
                  <strong>Strollers:</strong> welcome, no charge.
                </li>
                <li>
                  <strong>Folding wagons / Radio Flyer-style wagons:</strong>{" "}
                  permitted for $14.
                </li>
                <li>
                  <strong>Coolers:</strong> allowed; oversize coolers or large
                  luggage hauls will be charged.
                </li>
                <li>
                  <strong>
                    Bulk groceries, furniture, or building supplies:
                  </strong>{" "}
                  send those over on the freight boat.
                </li>
              </ul>
            </Faq>

            <Faq q="Can I bring my bike?">
              <p>
                Bikes are not permitted on the passenger ferry. No exceptions.
                Bikes must go over on the freight boat. For drop-off and
                schedule information, go to{" "}
                <a
                  href="https://fifreight.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  fifreight.com
                </a>
                .
              </p>
            </Faq>

            <Faq q="What's the difference between the passenger ferry and the freight boat?">
              <ul>
                <li>
                  <strong>Passenger ferry</strong> — runs all day, every day
                  during the season, to every community. For people, beach bags,
                  and reasonable luggage.
                </li>
                <li>
                  <strong>Freight boat</strong> — runs once-daily,
                  Monday–Saturday from the end of Maple Ave. For bikes,
                  furniture, bulk groceries, building supplies, and oversize
                  cargo. Same-day cutoff is 9:45am. See{" "}
                  <a
                    href="https://fifreight.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    fifreight.com
                  </a>{" "}
                  for more information.
                </li>
              </ul>
              <img
                src={freightboatimage}
                alt="Freight Boat"
                className="faq-image"
              />
            </Faq>

            <Faq q="Is the ferry accessible for wheelchairs, strollers, and wagons?">
              <p>
                Yes. Ramps are available on every vessel and our crews are
                trained to assist with embarkation and debarkation. If you'd
                like a crew member to meet you at boarding, call{" "}
                <i className="far fa-phone" /> 631.665.3600 ahead and we'll have
                someone ready.
              </p>
            </Faq>
          </Section>

          <Section title="On the Island" icon="umbrella-beach">
            <Faq q="Which community should I visit?">
              <p>
                Each Fire Island community has its own personality. Take our
                quick <Link to="/explore#quiz">Plan Your Trip Quiz</Link> to
                find the one that fits your day — or give us a call and we'll
                help you pick.
              </p>
            </Faq>

            <Faq q="What's the difference between bay-side and ocean-side beaches?">
              <p>Fire Island is a barrier beach with two coastlines:</p>
              <ul>
                <li>
                  <strong>Bay-side</strong> faces the Great South Bay — calmer
                  water, sunset views, dock-side dining.
                </li>
                <li>
                  <strong>Ocean-side</strong> faces the Atlantic — waves, surf,
                  the classic beach experience.
                </li>
              </ul>
              <p>
                A central path called the Burma Road runs the length of the
                island and connects all the communities, so you can walk between
                towns through the middle or along the ocean.
              </p>
            </Faq>

            <Faq q="Can I walk from one community to another?">
              <p>
                Yes. The Burma Road runs along the center of the island and
                connects every community. Walking along the ocean is also an
                option.
              </p>
            </Faq>

            <Faq q="Can I hire a private boat or charter?">
              <p>
                Yes — call Fire Island Water Taxi at{" "}
                <i className="far fa-phone" /> 631.665.8885 to book a cross-bay
                charter or private ride.
              </p>
            </Faq>

            <Faq q="Can I take a Water Taxi between communities on Fire Island?">
              <p>
                Yes — Fire Island Water Taxi runs beach-to-beach service
                parallel to the island, connecting communities by water. Call{" "}
                <i className="far fa-phone" /> 631.665.8885 for rates and
                current schedule.
              </p>
            </Faq>
          </Section>

          <hr className="faq-tail" />
          <h3>Still have a question?</h3>
          <p>
            Call us at <i className="far fa-phone" /> 631.665.3600 or email{" "}
            <i className="far fa-envelope" /> info@fireislandferries.com. If we
            hear the same question more than a few times, it ends up here.
          </p>
        </div>
      </div>
    );
  }
}

export default FaqPage;
